import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cartAPI, productsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Trash2, ArrowRight } from 'lucide-react';
import Layout from '@/components/Layout';
import { toast } from 'sonner';

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCart();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCart = async () => {
    try {
      const data = await cartAPI.getCart();
      setCartItems(data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to view cart');
        navigate('/login');
      } else {
        console.error('Error loading cart:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    try {
      await cartAPI.removeFromCart(productId);
      toast.success('Removed from cart');
      loadCart();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-custom">
            <div className="skeleton h-96 rounded-2xl"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="section-padding" data-testid="cart-page">
        <div className="container-custom">
          <h1 className="text-4xl font-bold mb-12">Shopping Cart</h1>

          {cartItems.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg mb-6">Your cart is empty</p>
              <Button onClick={() => navigate('/products')} className="btn-primary" data-testid="continue-shopping-button">
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item) => (
                  <Card key={item.cart_item_id} data-testid={`cart-item-${item.product_id}`}>
                    <CardContent className="p-6">
                      <div className="flex gap-6">
                        <img
                          src={item.product.images[0] || 'https://images.unsplash.com/photo-1518117823675-9a4bb23df61c'}
                          alt={item.product.name}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">{item.product.name}</h3>
                          <p className="text-muted-foreground text-sm mb-4">{item.product.description}</p>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">Quantity: {item.quantity}</p>
                              <p className="text-lg font-bold font-mono">KES {(item.product.price * item.quantity).toLocaleString()}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemove(item.product_id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`remove-${item.product_id}`}
                            >
                              <Trash2 size={20} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Order Summary */}
              <div>
                <Card className="sticky top-24">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-xl font-bold">Order Summary</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="font-medium">KES {calculateTotal().toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Shipping</span>
                        <span className="font-medium">Calculated at checkout</span>
                      </div>
                      <div className="border-t border-border pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="text-lg font-semibold">Total</span>
                          <span className="text-2xl font-bold font-mono" data-testid="cart-total">
                            KES {calculateTotal().toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate('/checkout')}
                      className="w-full btn-primary"
                      size="lg"
                      data-testid="checkout-button"
                    >
                      Proceed to Checkout <ArrowRight className="ml-2" size={20} />
                    </Button>
                    <Button
                      onClick={() => navigate('/products')}
                      variant="outline"
                      className="w-full"
                      data-testid="continue-shopping-button-summary"
                    >
                      Continue Shopping
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CartPage;